import React, { useState } from 'react';
import { Check, Close, Add } from '@mui/icons-material';
import Label from '../../../../ownerPortal/yourProperties/Label';
import {
    AttributeDefinitionState,
    AttributeValueState,
} from '../../../types/zitoneoServicesTypes';
import useTranslate from '../../../../../customHooks/useTranslate';
import Tag from '../../../../../muiComponents/Tag';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';

interface MultipleValueInputProps {
    values: AttributeValueState[];
    definition: AttributeDefinitionState;
    disabled: boolean;
    onAddValue: (value: string) => void;
    onRemoveValue: (valueId: string) => void;
    onEditValue: (valueId: string, newValue: string) => void;
    characterLimit: number;
}

const MultipleValueInput = (props: MultipleValueInputProps) => {
    const {
        values,
        definition,
        disabled,
        onAddValue,
        onRemoveValue,
        onEditValue,
        characterLimit,
    } = props;
    const [inputValue, setInputValue] = useState<string>('');
    const [editingValueId, setEditingValueId] = useState<string | null>(null);
    const [editingValue, setEditingValue] = useState<string>('');
    const translate = useTranslate();

    const handleAddValue = () => {
        if (inputValue.trim() === '') {
            return;
        }
        onAddValue(inputValue);
        setInputValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddValue();
        }
    };

    const handleDoubleClickTag = (valueId: string, currentValue: string) => {
        setEditingValueId(valueId);
        setEditingValue(currentValue);
    };

    const handleSaveEdit = () => {
        if (!editingValueId) {
            return;
        }
        if (editingValue.trim() === '') {
            setEditingValueId(null);
            return;
        }
        onEditValue(editingValueId, editingValue);
        setEditingValueId(null);
        setEditingValue('');
    };

    const handleCancelEdit = () => {
        setEditingValueId(null);
        setEditingValue('');
    };

    const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    return (
        <div className='flex-column gap-10'>
            <Label>
                {definition.Name}
                {definition.Required ? (
                    <span className='text-error-color'> *</span>
                ) : (
                    ''
                )}
            </Label>
            <div className='input-field__multiple-values__container'>
                {values.length ? (
                    <div className='flex flex-wrap gap-10'>
                        {values.map(value => {
                            const isEditing = editingValueId === value.Id;

                            if (isEditing) {
                                return (
                                    <div
                                        key={value.Id}
                                        className='flex gap-5 flex-align-center'
                                    >
                                        <TextInput
                                            autoFocus
                                            value={editingValue}
                                            handleChange={(
                                                _e: React.ChangeEvent<HTMLInputElement>,
                                                newValue: string
                                            ) =>
                                                setEditingValue(
                                                    newValue.slice(
                                                        0,
                                                        characterLimit
                                                    )
                                                )
                                            }
                                            onKeyDown={handleEditKeyDown}
                                            disabled={disabled}
                                            deleteIcon={false}
                                            sx={{
                                                width: '500px',
                                                backgroundColor:
                                                    'var(--background)',
                                            }}
                                            error={
                                                editingValue.length >=
                                                characterLimit
                                            }
                                            errorMessage={
                                                editingValue.length >=
                                                characterLimit
                                                    ? `${translate(
                                                          'maximum_character_limit_reached'
                                                      )}. ${characterLimit} ${translate(
                                                          'characters'
                                                      )}.`
                                                    : undefined
                                            }
                                            helperText={
                                                editingValue.length <
                                                characterLimit
                                                    ? `${
                                                          editingValue.length
                                                      } / ${characterLimit} ${translate(
                                                          'characters'
                                                      )}.`
                                                    : undefined
                                            }
                                            suffix={
                                                <>
                                                    <button
                                                        onClick={handleSaveEdit}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                        title={translate(
                                                            'save'
                                                        )}
                                                    >
                                                        <Check
                                                            fontSize='small'
                                                            style={{
                                                                color: 'var(--primary-400)',
                                                            }}
                                                        />
                                                    </button>
                                                    <button
                                                        onClick={
                                                            handleCancelEdit
                                                        }
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                        title={translate(
                                                            'cancel'
                                                        )}
                                                    >
                                                        <Close
                                                            fontSize='small'
                                                            style={{
                                                                color: 'var(--text)',
                                                            }}
                                                        />
                                                    </button>
                                                </>
                                            }
                                        />
                                    </div>
                                );
                            }

                            return (
                                <Tag
                                    key={value.Id}
                                    variant='surface'
                                    color='grey'
                                    text={value.Value}
                                    endIcon='Close'
                                    iconProps={{
                                        onClick: () => onRemoveValue(value.Id),
                                    }}
                                    onClick={() =>
                                        handleDoubleClickTag(
                                            value.Id,
                                            value.Value
                                        )
                                    }
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: '400',
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                    }}
                                    titleText='double_click_to_edit'
                                />
                            );
                        })}
                    </div>
                ) : (
                    ''
                )}

                <div className='flex gap-5 flex-align-center'>
                    <TextInput
                        value={inputValue}
                        handleChange={(
                            _e: React.ChangeEvent<HTMLInputElement>,
                            newValue: string
                        ) => setInputValue(newValue.slice(0, characterLimit))}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        placeholder={translate('add_value')}
                        deleteIcon={false}
                        sx={{
                            flex: 1,
                            backgroundColor: 'var(--background)',
                            paddingBlock: '4px',
                        }}
                        suffix={
                            <button
                                onClick={handleAddValue}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'var(--primary-400)',
                                }}
                                title={translate('add_value')}
                            >
                                <Add fontSize='small' />
                            </button>
                        }
                        error={inputValue.length >= characterLimit}
                        errorMessage={
                            inputValue.length >= characterLimit
                                ? `${translate(
                                      'maximum_character_limit_reached'
                                  )}. ${characterLimit} ${translate(
                                      'characters'
                                  )}.`
                                : undefined
                        }
                        helperText={
                            inputValue.length < characterLimit
                                ? `${
                                      inputValue.length
                                  } / ${characterLimit} ${translate(
                                      'characters'
                                  )}.`
                                : undefined
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default MultipleValueInput;
